import * as React from 'react';
import { getClosestCoordinateByStep } from '../datasheet.utils';
import { IDatasheetEditorElement } from '../datasheet.types';
import { IDatasheetComponentDto } from '@common/dto/datasheet/datasheet-component';

type Coordinates = { y: number; x: number };

type DragStart = {
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
};

export type DatasheetElementControllerProps<T extends IDatasheetComponentDto = IDatasheetComponentDto> = IDatasheetEditorElement<T> & {
  minWidth: number;
  minHeight: number;
};

export enum DatasheetElementResizeDirection {
  se = 'se',
  sw = 'sw',
  ne = 'ne',
  nw = 'nw',
  n = 'n',
  s = 's',
  w = 'w',
  e = 'e',
}

export const useDatasheetElementController = <T extends IDatasheetComponentDto = IDatasheetComponentDto>(props: DatasheetElementControllerProps<T>) => {
  const { 
    containerRef, 
    minHeight,
    minWidth,
    isSelected: isSelectedOnDatasheet, 
    datasheetElement,
    onSelect,
    onCoordinatesChange,
    onResize,
    datasheetGridSettings,
    resizeHandlerStrategy,
    isTabOrderSettingActive,
  } = props;

  const [headingSize, setHeadingSize] = React.useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [coordinates, setCoordinates] = React.useState<Coordinates>({ x: datasheetElement.coordinates.x, y: datasheetElement.coordinates.y });
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [dragStart, setDragStart] = React.useState<DragStart>({ startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const [size, setSize] = React.useState({ width: datasheetElement.width, height: datasheetElement.height });
  const [resizing, setResizing] = React.useState<{ active: boolean; direction?: DatasheetElementResizeDirection }>({ active: false, direction: DatasheetElementResizeDirection.e });

  const movableElementRef = React.useRef<HTMLDivElement>(null);

  const horizontalGridStep = datasheetGridSettings.snapToGrid ? datasheetGridSettings.horizontalGrid : 1;
  const verticalGridStep = datasheetGridSettings.snapToGrid ? datasheetGridSettings.verticalGrid : 1;

  React.useEffect(() => {
    if (datasheetElement.coordinates.x !== coordinates.x || datasheetElement.coordinates.y !== coordinates.y) {
      setCoordinates({ x: datasheetElement.coordinates.x, y: datasheetElement.coordinates.y });
    }
  }, [datasheetElement.coordinates.x, datasheetElement.coordinates.y]);

  React.useEffect(() => {
    if (datasheetElement.width !== size.width || datasheetElement.height !== size.height) {
      setSize({ width: datasheetElement.width, height: datasheetElement.height });
    }
  }, [datasheetElement.width, datasheetElement.height]);

  const startDrag = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();
    setIsDragging(true);
    setDragStart({
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - coordinates.x,
      offsetY: e.clientY - coordinates.y,
    });
  }, [coordinates.x, coordinates.y]);

  const handleDragging = (e: MouseEvent) => {
    const datasheetContainer = containerRef.current;
    
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();

    if (!isDragging || !datasheetContainer) {
      return;
    }

    const deltaX = e.clientX - dragStart.startX;
    const deltaY = e.clientY - dragStart.startY;

    if (Math.abs(deltaX) >= horizontalGridStep || Math.abs(deltaY) >= verticalGridStep) {
      const stepsX = Math.round(deltaX / horizontalGridStep);
      const stepsY = Math.round(deltaY / verticalGridStep);

      const newX = coordinates.x + stepsX * horizontalGridStep;
      const newY = coordinates.y + stepsY * verticalGridStep;

      const computedX = getClosestCoordinateByStep(
        Math.max(
          horizontalGridStep, 
          newX
        ), 
        horizontalGridStep
      );
      const computedY = getClosestCoordinateByStep(
        Math.max(
          verticalGridStep, 
          newY
        ), 
        verticalGridStep
      );

      const nextCoordinates = {
        x: computedX,
        y: computedY,
      };

      const coordinatesChanged = nextCoordinates.x !== coordinates.x || nextCoordinates.y !== coordinates.y;

      if (coordinatesChanged) {
        setCoordinates(nextCoordinates);
      }

      setDragStart((prevDragStart) => ({
        ...prevDragStart,
        startX: e.clientX,
        startY: e.clientY,
      }));

      if (resizeHandlerStrategy === 'immediate' && coordinatesChanged) {
        onCoordinatesChange(datasheetElement.id, { coordinates: nextCoordinates });
      }
    }
  };

  const resetDragging = (e: MouseEvent) => {
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();
    setIsDragging(false);

    const coordinatesChanged = datasheetElement.coordinates.x !== coordinates.x || datasheetElement.coordinates.y !== coordinates.y;

    if (coordinatesChanged) {
      onCoordinatesChange(datasheetElement.id, { coordinates });
    }
  };

  React.useEffect(() => {
    if (isDragging && !isTabOrderSettingActive) {
      containerRef.current?.addEventListener('mousemove', handleDragging);
      containerRef.current?.addEventListener('mouseup', resetDragging);
    }

    return () => {
      containerRef.current?.removeEventListener('mousemove', handleDragging);
      containerRef.current?.removeEventListener('mouseup', resetDragging);
    };
  }, [isDragging, dragStart, coordinates, onCoordinatesChange, isTabOrderSettingActive]);

  const handleResize = (e: MouseEvent) => {
    const datasheetContainer = containerRef.current;

    if (resizing.active && movableElementRef.current && datasheetContainer) {
      const datasheetContainerRect = datasheetContainer.getBoundingClientRect();
      const datasheetElementRect = movableElementRef.current.getBoundingClientRect();
      const newSize = { ...size };

      if (resizing.direction?.includes('e')) {
        newSize.width = e.clientX - datasheetElementRect.x;
      }

      if (resizing.direction?.includes('s')) {
        newSize.height = e.clientY - datasheetElementRect.y;
      }

      if (resizing.direction?.includes('w')) {
        const minX = datasheetContainerRect.x + horizontalGridStep;
        newSize.width = datasheetElementRect.right - Math.max(e.clientX, minX);

        setCoordinates((prev) => ({ 
          ...prev, 
          x: minX > e.clientX
            ? horizontalGridStep 
            : e.clientX - datasheetContainerRect.left
        }));
      }

      if (resizing.direction?.includes('n')) {
        const minY = datasheetContainerRect.y + verticalGridStep;
        newSize.height = datasheetElementRect.bottom - Math.max(e.clientY, minY);
        
        setCoordinates((prev) => ({ 
          ...prev, 
          y: minY > e.clientY
            ? verticalGridStep 
            : e.clientY - datasheetContainerRect.y 
        }));
      }

      const finalSize = {
        width: Math.max(minWidth, newSize.width),
        height: Math.max(minHeight, newSize.height),
      };

      setSize(finalSize);
      onResize(
        datasheetElement.id, 
        {
          width: finalSize.width,
          height: finalSize.height,
        }
      );
    }
  };

  const resetResizing = (e: MouseEvent) => {
    const callToPreventShowingSelectionBox = () => {
      e.stopPropagation();
    };

    callToPreventShowingSelectionBox();
    setResizing({ active: false, direction: undefined });
    onResize(
      datasheetElement.id, 
      {
        width: size.width,
        height: size.height,
        coordinates,
      }
    );
  };

  React.useEffect(() => {
    if (resizing.active && !isTabOrderSettingActive) {
      containerRef.current?.addEventListener('mousemove', handleResize);
      containerRef.current?.addEventListener('mouseup', resetResizing);
    }

    return () => {
      containerRef.current?.removeEventListener('mousemove', handleResize);
      containerRef.current?.removeEventListener('mouseup', resetResizing);
    };
  }, [resizing, coordinates, size, isTabOrderSettingActive]);

  const handleResizing = React.useCallback((e: React.MouseEvent<HTMLDivElement>, direction: DatasheetElementResizeDirection) => {
    e.stopPropagation();
    setResizing({ active: true, direction });
  }, []);

  const datasheetElementSelected = React.useMemo(() => {
    return isSelectedOnDatasheet(datasheetElement);
  }, [isSelectedOnDatasheet, datasheetElement]);

  const handleDatasheetElementMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!datasheetElementSelected) {
      onSelect(datasheetElement, e);
    }

    startDrag(e);
  }, [onSelect, datasheetElement, startDrag, datasheetElementSelected]);

  const handleHeadingSizeChange = React.useCallback((size: { width: number; height: number }) => {
    setHeadingSize(size);
  }, []);

  return {
    handleDatasheetElementMouseDown,
    datasheetElementSelected,
    coordinates,
    movableElementRef,
    handleResizing,
    size,
    handleHeadingSizeChange,
    headingSize,
  };
};